import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControl,
  Grid, LinearProgress, List, ListItemButton, ListItemIcon, ListItemText,
  ListSubheader, MenuItem, Paper, Select, Snackbar, Stack, Switch, ThemeProvider,
  Tooltip, Typography,
} from "@mui/material";
import RefreshIcon      from "@mui/icons-material/Refresh";
import PrintIcon        from "@mui/icons-material/Print";
import CancelIcon       from "@mui/icons-material/Cancel";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import SwapHorizIcon    from "@mui/icons-material/SwapHoriz";
import EventBusyIcon    from "@mui/icons-material/EventBusy";
import SettingsIcon     from "@mui/icons-material/Settings";
import PeopleAltIcon    from "@mui/icons-material/PeopleAlt";
import HotelIcon        from "@mui/icons-material/Hotel";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import PercentIcon      from "@mui/icons-material/Percent";
import GroupIcon        from "@mui/icons-material/Group";
import MedicationIcon   from "@mui/icons-material/Medication";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import LockIcon         from "@mui/icons-material/Lock";
import axios from "axios";
import { toast } from "react-toastify";
import adtBedService from "../../../../services/AdtBedService";

import { NURSE_COLORS, nurseTheme } from "../../../nurse-dashboard/theme";
import { StatCard }     from "../../../nurse-dashboard/components";
import CancelModal           from "./CancelModal";
import AllocationSuccessModal from "./AllocationSuccessModal";
import HousekeepingSection   from "./HousekeepingSection";
import HousekeepingSettingsAdmin from "../settings/HousekeepingSettingsAdmin";
import StickerPrintModal     from "../../components/StickerPrintModal";
import HousekeepingPrompt    from "../../components/HousekeepingPrompt";
import RetainBedModal        from "../bed-retention/RetainBedModal";
import { raiseOccupancyApproval, setBedApprovalRequirement } from "../../api/occupancyApprovalApi";
import { describeAccrual, startBedAccrual } from "../../api/autoChargeApi";
import useBedCensusData from "../../hooks/useBedCensusData";
import BedCensusTable from "../../components/BedCensusTable";
import BedCensusFilters from "../../components/BedCensusFilters";
import RaiseTransferRequestModal from "../bed-transfer-requisition/RaiseTransferRequestModal";
import { statusCfg } from "../../utils/bedStatus";
import { usePermissions } from "../../../login/PermissionContext";
import { isActionAllowedForRole, BED_DASHBOARD_ACTIONS } from "../../utils/bedDashboardRoles";
import { formatIstDate } from "../../../../utils/istDateTime";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function calcAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? age : null;
}
function fmtGender(v) {
  const n = String(v || "").trim().toUpperCase();
  return n === "F" ? "F" : n === "M" ? "M" : v || "";
}
// admitted_at is a UTC instant. Taking its date half directly put an evening
// admission — anything after 18:30 IST — on the previous day.
function fmtDate(ts) {
  return formatIstDate(ts, "—");
}
function normalizeAdmissions(payload) {
  const source = payload?.data || payload?.admission_list || payload?.results ||
    (Array.isArray(payload) ? payload : []);
  const list = Array.isArray(source) ? source : source ? [source] : [];
  return list.map((item, i) => ({
    id:            item?.admission_id || i + 1,
    admissionId:   item?.admission_id ?? null,
    patientName:   item?.patient?.name || "—",
    patientId:     item?.patient?.patient_id || "",
    age:           item?.patient?.age ?? calcAge(item?.patient?.dob),
    gender:        fmtGender(item?.patient?.gender),
    admissionDate: fmtDate(item?.admitted_at),
    status:        item?.status || "Admitted",
    bedCode:       item?.bed_id || "—",
    bedTypeCode:   item?.bed_type_code || "",
    departmentId:  item?.department_id || "",
    location:      item?.location || "",
  }));
}

// ─── Bed action dialog ────────────────────────────────────────────────────────
function BedActionDialog({ open, bed, patient, onClose, onPrint, onClean, onCancel, onTransfer, onRetain, can }) {
  if (!bed) return null;
  const cfg = statusCfg(bed.status);
  // Actions below only make sense for a bed genuinely occupied by a patient —
  // not "reserved" (mid-transfer / held via BedRetention) or "cleaning".
  const isOccupied = String(bed.status || "").toLowerCase() === "occupied";
  // Retention is the mirror image: the backend refuses to retain anything that
  // isn't "available" (manage_bed_retention.py), so only offer it there.
  const isAvailable = String(bed.status || "").toLowerCase() === "available";
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Bed {bed.code}</Typography>
          <Chip size="small" label={cfg.label} color={cfg.chipColor} />
        </Stack>
        {bed.location && <Typography variant="caption" color="text.secondary">{bed.location}</Typography>}
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {patient ? (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: NURSE_COLORS.primary, width: 36, height: 36, fontSize: 14 }}>
                {patient.patientName.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="subtitle2">{patient.patientName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[patient.patientId, patient.age != null ? `${patient.age}${patient.gender}` : null, `Adm: ${fmtDate(patient.admittedAt)}`].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ) : (
          <Typography variant="body2" color="text.secondary">No patient linked to this bed.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: "column", gap: 1, px: 2, pb: 2, alignItems: "stretch" }}>
        {isOccupied && patient && can(BED_DASHBOARD_ACTIONS.PRINT_STICKER) && (
          <Button fullWidth variant="contained" startIcon={<PrintIcon />}
            onClick={() => { onClose(); onPrint(patient.admissionId); }}
            sx={{ bgcolor: NURSE_COLORS.primary, "&:hover": { bgcolor: NURSE_COLORS.primaryDark } }}>
            Print Sticker
          </Button>
        )}
        {isOccupied && patient && can(BED_DASHBOARD_ACTIONS.TRANSFER_PATIENT) && (
          <Button fullWidth variant="outlined" startIcon={<SwapHorizIcon />}
            onClick={() => { onClose(); onTransfer(bed, patient); }}
            sx={{ color: NURSE_COLORS.primary, borderColor: NURSE_COLORS.primary }}>
            Transfer Patient
          </Button>
        )}
        {isOccupied && can(BED_DASHBOARD_ACTIONS.MARK_CLEANING) && (
          <Button fullWidth variant="outlined" startIcon={<CleaningServicesIcon />}
            onClick={() => { onClose(); onClean(bed.code, patient?.admissionId); }}
            sx={{ color: NURSE_COLORS.info, borderColor: NURSE_COLORS.info, "&:hover": { bgcolor: "#EFF6FF" } }}>
            Mark Bed for Cleaning
          </Button>
        )}
        {isAvailable && can(BED_DASHBOARD_ACTIONS.RETAIN_BED) && (
          <Button fullWidth variant="outlined" startIcon={<EventBusyIcon />}
            onClick={() => { onClose(); onRetain(bed.code); }}
            sx={{ color: NURSE_COLORS.warning, borderColor: NURSE_COLORS.warning }}>
            Retain Bed
          </Button>
        )}
        {isOccupied && patient && can(BED_DASHBOARD_ACTIONS.CANCEL_ADMISSION) && (
          <Button fullWidth variant="outlined" color="error" startIcon={<CancelIcon />}
            onClick={() => { onClose(); onCancel(patient); }}>
            Cancel Admission
          </Button>
        )}
        <Button onClick={onClose} sx={{ color: NURSE_COLORS.textSecondary }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function BedAllocation() {
  const {
    beds, admissionByBed, loading, reload: reloadCensus,
    filters, setFilters, search, setSearch, wards, rooms,
  } = useBedCensusData();
  const { userInfo } = usePermissions();
  const roleName = userInfo?.role_permission?.[0]?.role;
  const can = (action) => isActionAllowedForRole(action, roleName);

  const [summary,        setSummary]        = useState(null);
  const [activeSection,  setActiveSection]  = useState("census");

  const [patients,       setPatients]       = useState([]);
  const [patientsLoading,setPatientsLoading]= useState(false);
  const [selectedBeds,   setSelectedBeds]   = useState({});
  const [bedOptions,     setBedOptions]     = useState({});
  const [loadingBeds,    setLoadingBeds]    = useState({});
  const [allocating,     setAllocating]     = useState(null);

  const [activeBed,      setActiveBed]      = useState(null);
  const [cancelTarget,   setCancelTarget]   = useState(null);
  const [successResult,  setSuccessResult]  = useState(null);
  const [stickerId,      setStickerId]      = useState(null);
  const [cleaningBed,    setCleaningBed]    = useState(null);
  const [retainTarget,   setRetainTarget]   = useState(null);
  const [approvalNeeded, setApprovalNeeded] = useState(null);
  const [snack,          setSnack]          = useState(null);

  // G11-B config: which beds are flagged is_approval_required. `savingApproval`
  // holds the bed code mid-request; `approvalOverride` mirrors the value we
  // just saved so the Switch doesn't snap back while the census refetches, and
  // is dropped for that bed once the refetch lands.
  const [savingApproval,   setSavingApproval]   = useState(null);
  const [approvalOverride, setApprovalOverride] = useState({});

  // ── Transfer requisition ─────────────────────────────────────────────────
  const [xfer, setXfer] = useState(null); // { admissionId, fromBedCode, fromBed }

  const authToken = sessionStorage.getItem("accessToken") || "";
  const hdrs = { Authorization: `Bearer ${authToken}` };
  const BASE  = process.env.REACT_APP_BACKEND_ROUTE_URL || "https://manage-dev.hlthtek.com/app/api/";

  // ── Loaders ──────────────────────────────────────────────────────────────
  // Bed + occupant census now comes from useBedCensusData() (shared with the
  // nurse dashboard). This just refreshes the summary stat cards alongside it.
  const reloadSummary = useCallback(async () => {
    try {
      const sumRes = await axios.get(`${BASE}nurse_dashboard/summary/`, { headers: hdrs });
      if (sumRes?.data?.status === "Success") setSummary(sumRes.data.data);
    } catch (err) {
      setSnack({ sev: "error", msg: err?.response?.data?.message || "Failed to load summary." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(() => { reloadSummary(); reloadCensus(); }, [reloadSummary, reloadCensus]);

  const reloadPatients = useCallback(async () => {
    setPatientsLoading(true);
    try {
      const res = await axios.get(`${BASE}ipd_admission/?status=Admitted`, { headers: hdrs });
      const norm = normalizeAdmissions(res?.data);
      setPatients(norm);
      const sel = {}, opts = {};
      norm.forEach((p) => {
        const b = p.bedCode !== "—" ? p.bedCode : "";
        sel[p.id]  = b;
        opts[p.id] = b ? [{ bedId: b, label: `${b} (Current)`, isCurrent: true }] : [];
      });
      setSelectedBeds(sel);
      setBedOptions(opts);
    } catch { setPatients([]); }
    finally { setPatientsLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { reload(); reloadPatients(); }, [reload, reloadPatients]);

  const fetchBedsForPatient = async (patient) => {
    const key = patient.id;
    if (loadingBeds[key]) return;
    setLoadingBeds((p) => ({ ...p, [key]: true }));
    try {
      // A bed has no department since the ward/department decoupling, so the
      // only scope is the admission's own bed type — and an admission created
      // without one (Emergency's Admitted disposition, emergency_to_ipd) gets
      // every bed in the facility, labelled with its ward (bed type) so the
      // user picks the ward and the bed in one go.
      const scope = patient.bedTypeCode
        ? `?bed_type_code=${encodeURIComponent(patient.bedTypeCode)}`
        : "";
      const res = await axios.get(`${BASE}ipd_bed/${scope}`, { headers: hdrs });
      // Label by Ward > Bed. The ward is room_category (ICU / General / ITU /
      // Private) — b.ward_name holds a department name and is not read (see
      // useBedCensusData). `location` ("Floor 1 - Room 101") is kept only as a
      // last-resort fallback; on its own it can't tell two beds of the same
      // type apart.
      const available = (res?.data?.data || [])
        .filter((b) => String(b.status || "").toLowerCase() === "available")
        .map((b) => ({
          bedId: b.code,
          label:
            ([
              b.code,
              patient.bedTypeCode ? null : b.bed_type_code,
              b.room_category_name || b.room_category_code,
              b.room_name,
            ]
              .filter(Boolean)
              .join(" · ") || b.code + (b.location ? ` · ${b.location}` : "")) +
            // G11-B: say so up front rather than letting the user pick a gated
            // bed and only learn it needs approval when Allocate is refused.
            (b.is_approval_required ? " · needs approval" : ""),
        }))
        .filter((b) => b.bedId);
      const cur = patient.bedCode !== "—" ? patient.bedCode : "";
      if (cur && !available.some((b) => b.bedId === cur))
        available.unshift({ bedId: cur, label: `${cur} (Current)`, isCurrent: true });
      setBedOptions((p) => ({ ...p, [key]: available }));
      if (!available.length) setSnack({ sev: "info", msg: "No available beds for this patient." });
    } catch { setSnack({ sev: "error", msg: "Failed to fetch beds." }); }
    finally { setLoadingBeds((p) => ({ ...p, [key]: false })); }
  };

  const handleAllocate = async (patient) => {
    if (!patient.admissionId) { setSnack({ sev: "warning", msg: "Admission ID missing." }); return; }
    const oldBed = patient.bedCode !== "—" ? patient.bedCode : "";
    const newBed = selectedBeds?.[patient.id] || "";
    if (!newBed) { setSnack({ sev: "warning", msg: "Please select a bed." }); return; }
    setAllocating(patient.id);
    try {
      // old_bed_id is optional server-side precisely for an admission created
      // with no bed (Emergency's Admitted disposition, emergency_to_ipd) —
      // omit it rather than refuse the patient a first bed.
      const res = await axios.post(
        `${BASE}ipd_bed_allocation/`,
        {
          admission_id: patient.admissionId,
          ...(oldBed ? { old_bed_id: oldBed } : {}),
          new_bed_id: newBed,
        },
        { headers: { ...hdrs, "Content-Type": "application/json" } }
      );
      if (res?.data?.status === "Success") {
        try { await axios.post(`${BASE}ipd_admission/${patient.admissionId}/billing_setup/`, {}, { headers: hdrs }); }
        catch { /* billing errors shown separately */ }
        // Register the recurring bed-day charge the daily job bills from. Runs
        // after billing_setup/ because start needs the billing account and
        // encounter ids that call provisions. Awaited before the modal opens so
        // it shows one settled result; never blocks the allocation, which has
        // already happened.
        const accrual = describeAccrual(await startBedAccrual(patient.admissionId));
        setSuccessResult({ admission_id: res.data.admission_id ?? patient.admissionId, from_bed_code: res.data.from_bed_code, to_bed_code: res.data.to_bed_code ?? newBed, accrual });
        setSnack({ sev: "success", msg: res.data.message || "Bed allocated." });
      } else if (res.data.requires_approval) {
        // G11-B: this bed is flagged is_approval_required and has no live
        // approval yet — offer to raise the request right here.
        setApprovalNeeded({ admissionId: patient.admissionId, bedCode: res.data.bed_code || newBed });
        setSnack({ sev: "warning", msg: res.data.message || "This bed requires supervisor approval." });
      } else {
        setSnack({ sev: "error", msg: res.data.message || "Allocation failed." });
      }
    } catch (err) {
      setSnack({ sev: "error", msg: err?.response?.data?.message || "Allocation failed." });
    } finally { setAllocating(null); }
  };

  const handleRaiseApproval = async () => {
    if (!approvalNeeded) return;
    try {
      const data = await raiseOccupancyApproval({
        admissionId: approvalNeeded.admissionId,
        bedCode: approvalNeeded.bedCode,
      });
      if (data?.status === "Success") {
        setSnack({ sev: "success", msg: data.message || "Approval request raised. A supervisor must approve it from the Approvals Center before this bed can be allocated." });
      } else {
        setSnack({ sev: "error", msg: data?.message || "Could not raise the approval request." });
      }
    } catch (err) {
      setSnack({ sev: "error", msg: err?.response?.data?.message || "Could not raise the approval request." });
    } finally {
      setApprovalNeeded(null);
    }
  };

  // G11-B: flag/unflag a bed as needing supervisor approval before it can be
  // occupied. This is the switch that ARMS the gate enforced by
  // ipd_bed_allocation/ and the transfer executor — without it the whole
  // occupancy-approval feature stays dormant, since nothing else sets the flag.
  const handleToggleApproval = async (bed, required) => {
    setSavingApproval(bed.code);
    setApprovalOverride((prev) => ({ ...prev, [bed.code]: required }));
    try {
      const data = await setBedApprovalRequirement(bed.code, required);
      if (data?.status === "Success") {
        setSnack({
          sev: "success",
          msg: required
            ? `Bed ${bed.code} now needs supervisor approval before allocation.`
            : `Bed ${bed.code} no longer needs approval.`,
        });
        await reload();
      } else {
        setSnack({ sev: "error", msg: data?.message || "Could not update the bed." });
      }
    } catch (err) {
      setSnack({ sev: "error", msg: err?.response?.data?.message || "Could not update the bed." });
    } finally {
      // Drop the override either way: on success the refetched row carries the
      // new value, on failure the Switch must fall back to the stored one.
      setApprovalOverride((prev) => {
        const next = { ...prev };
        delete next[bed.code];
        return next;
      });
      setSavingApproval(null);
    }
  };

  // ── Transfer requisition (ADR 0003 — approval required, no direct-execute) ──
  const openTransfer = (bed, patient) => {
    const resolved = patient ?? admissionByBed[bed.code];
    if (!resolved?.admissionId) {
      setSnack({ sev: "error", msg: "Missing admission for this bed" });
      return;
    }
    setXfer({ admissionId: resolved.admissionId, fromBedCode: bed.code, fromBed: bed });
  };
  const handleRaised = () => {
    setXfer(null);
    reload();
    setSnack({ sev: "success", msg: "Transfer request raised — awaiting approval." });
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const stats = summary ? [
    { icon: <PeopleAltIcon />,         label: "Admitted",       value: summary.patients_admitted,          color: NURSE_COLORS.primary },
    { icon: <HotelIcon />,             label: "Total Beds",     value: summary.beds?.total,                color: NURSE_COLORS.primaryLight },
    { icon: <LocalHospitalIcon />,     label: "Occupied",       value: summary.beds?.occupied,             color: NURSE_COLORS.danger },
    { icon: <CheckCircleIcon />,       label: "Vacant",         value: summary.beds?.vacant,               color: NURSE_COLORS.success },
    { icon: <PercentIcon />,           label: "Occupancy",      value: `${summary.beds?.occupancy_pct ?? 0}%`, color: NURSE_COLORS.warning },
    { icon: <GroupIcon />,             label: "Nurses on Duty", value: summary.staffing?.nurses_on_duty,   color: NURSE_COLORS.info },
    { icon: <MedicationIcon />,        label: "Meds Due",       value: summary.work?.meds_due,             color: NURSE_COLORS.danger },
    { icon: <AssignmentTurnedInIcon />,label: "Tasks Pending",  value: summary.work?.tasks_pending,        color: NURSE_COLORS.warning },
  ] : [];

  const NAV_ITEMS = [
    { key: "census", label: "Bed Census", icon: <HotelIcon fontSize="small" /> },
    { key: "awaiting", label: "Awaiting Allocation", icon: <PeopleAltIcon fontSize="small" />, badge: patients.length },
    { key: "housekeeping", label: "Housekeeping", icon: <CleaningServicesIcon fontSize="small" /> },
    // The standalone /housekeeping_settings route exists but has no landing-page
    // tile, so the auto-housekeeping toggle was unreachable in practice. Surface
    // it here, next to the Housekeeping queue it governs.
    ...(can(BED_DASHBOARD_ACTIONS.MANAGE_SETTINGS)
      ? [{ key: "settings", label: "Settings", icon: <SettingsIcon fontSize="small" /> }]
      : []),
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  // Scoped nurseTheme + navy sidebar so this screen reads as part of the same
  // module as the nurse dashboard (NurseDashboardShell's SidebarContent), not
  // as a differently-styled page that happens to share its palette.
  return (
    <ThemeProvider theme={nurseTheme}>
    <Box sx={{ bgcolor: NURSE_COLORS.pageBg, minHeight: "100vh", display: "flex" }}>

      {/* Left nav — keeps Census / Awaiting Allocation / Housekeeping on
          separate screens instead of one long scrolling page. */}
      <Box sx={{
        width: 248, flexShrink: 0, bgcolor: NURSE_COLORS.primary, color: "#fff",
        display: { xs: "none", md: "block" },
      }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: "rgba(33,205,192,0.15)", display: "grid", placeItems: "center" }}>
            <HotelIcon sx={{ color: NURSE_COLORS.accent, fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ color: "#fff", lineHeight: 1.1 }}>IPD Beds</Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>Census &amp; Allocation</Typography>
          </Box>
        </Box>
        <List
          dense
          disablePadding
          sx={{ px: 1, pb: 2 }}
          subheader={
            <ListSubheader
              disableSticky
              sx={{ bgcolor: "transparent", color: "rgba(255,255,255,0.5)", fontSize: 11, letterSpacing: 0.8, textTransform: "uppercase", lineHeight: 2.2, px: 1.5 }}
            >
              Bed Management
            </ListSubheader>
          }
        >
          {NAV_ITEMS.map((item) => {
            const active = activeSection === item.key;
            return (
              <ListItemButton
                key={item.key}
                onClick={() => setActiveSection(item.key)}
                sx={{
                  borderRadius: 1.5, mb: 0.25, px: 1.5, py: 0.9,
                  color: active ? "#fff" : "rgba(255,255,255,0.78)",
                  bgcolor: active ? "rgba(33,205,192,0.18)" : "transparent",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                  borderLeft: active ? `3px solid ${NURSE_COLORS.accent}` : "3px solid transparent",
                }}
              >
                <ListItemIcon sx={{ color: "inherit", minWidth: 32 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: active ? 600 : 500 } }} />
                {!!item.badge && (
                  <Chip size="small" label={item.badge}
                    sx={{ bgcolor: NURSE_COLORS.accent, color: NURSE_COLORS.primary, height: 20, fontSize: 11 }} />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, p: { xs: 1.5, md: 3 } }}>

      {/* Below md the sidebar is hidden — the sections become a tab strip so
          they stay reachable without porting in the shell's Drawer. */}
      <Stack direction="row" spacing={1} sx={{ display: { xs: "flex", md: "none" }, mb: 2, overflowX: "auto" }}>
        {NAV_ITEMS.map((item) => (
          <Chip
            key={item.key}
            icon={item.icon}
            label={item.label}
            onClick={() => setActiveSection(item.key)}
            color={activeSection === item.key ? "primary" : "default"}
            variant={activeSection === item.key ? "filled" : "outlined"}
          />
        ))}
      </Stack>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" color={NURSE_COLORS.textPrimary}>
            {NAV_ITEMS.find((i) => i.key === activeSection)?.label || "Bed Census & Allocation"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live occupancy · click occupied rows for actions
          </Typography>
        </Box>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => { reload(); reloadPatients(); }}
          sx={{ color: NURSE_COLORS.primary, border: `1px solid ${NURSE_COLORS.border}`, borderRadius: 20, textTransform: "none" }}>
          Refresh
        </Button>
      </Stack>

      {/* Summary stat cards */}
      {activeSection === "census" && stats.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Four across, matching the nurse dashboard's stat row. The old
              lg={1.5} squeezed all eight cards onto one row (~12.5% each), so
              longer labels wrapped to three lines. */}
          {stats.map((s) => (
            <Grid item xs={6} sm={4} md={3} key={s.label}>
              <StatCard icon={s.icon} label={s.label} value={s.value ?? "—"} color={s.color} />
            </Grid>
          ))}
        </Grid>
      )}

      {activeSection === "census" && (
      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="subtitle1">Bed Status</Typography>
            <Typography variant="caption" color="text.secondary">{beds.length} beds · click a row to act</Typography>
          </Box>
          <BedCensusFilters
            filters={filters} setFilters={setFilters}
            wards={wards} rooms={rooms}
            search={search} onSearchChange={setSearch}
          />
        </Stack>

        {loading && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}

        <BedCensusTable
          beds={beds}
          admissionByBed={admissionByBed}
          loading={loading}
          paginate
          rowsPerPageOptions={[25, 50, 100]}
          onRowClick={(bed) => setActiveBed(bed)}
          extraColumns={[{
            header: "Approval",
            render: (bed) => {
              const required = approvalOverride[bed.code] ?? Boolean(bed.is_approval_required);
              // Only a supervisor/admin may arm the gate (MANAGE_SETTINGS —
              // same bar as the facility-wide housekeeping settings, since
              // this changes how a bed behaves for everyone). Everyone else
              // still SEES the flag: a nurse should know a bed is gated
              // before she picks it, not after the allocation is refused.
              if (!can(BED_DASHBOARD_ACTIONS.MANAGE_SETTINGS)) {
                return required
                  ? <Chip size="small" variant="outlined" icon={<LockIcon />} label="Approval"
                      sx={{ height: 22, fontSize: 11, color: NURSE_COLORS.warning,
                            borderColor: NURSE_COLORS.warning }} />
                  : <Typography variant="body2" color="text.secondary">—</Typography>;
              }
              return (
                <Tooltip title={required
                  ? "A supervisor must approve each allocation to this bed"
                  : "Require supervisor approval before this bed can be occupied"}>
                  <Switch
                    size="small"
                    checked={required}
                    disabled={savingApproval === bed.code}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleToggleApproval(bed, e.target.checked)}
                  />
                </Tooltip>
              );
            },
          }]}
          renderActions={(bed, patient) => {
            const cfg = statusCfg(bed.status);
            const isOccupied = String(bed.status || "").toLowerCase() === "occupied";
            // Sticker / Transfer still only make sense for a bed genuinely
            // occupied by a patient — not "reserved" (mid-transfer / held via
            // BedRetention) or "cleaning".
            const showOccupiedActions = cfg.clickable && isOccupied && patient;
            // Epic 5: cleaning is decoupled from Bed.status, so "Clean" is offered
            // on ANY bed (role-gated), not just occupied ones. If it's already
            // flagged (cleaning_state set), show a badge instead of the button.
            const flaggedForCleaning =
              bed.cleaning_state === "needs_cleaning" || bed.cleaning_state === "cleaning";
            const showClean =
              can(BED_DASHBOARD_ACTIONS.MARK_CLEANING) && !flaggedForCleaning;
            // Retain mirrors the backend rule: only an "available" bed can be
            // held (manage_bed_retention.py rejects anything else).
            const showRetain =
              String(bed.status || "").toLowerCase() === "available" &&
              can(BED_DASHBOARD_ACTIONS.RETAIN_BED);
            if (!showOccupiedActions && !showClean && !flaggedForCleaning && !showRetain) return null;
            return (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                {showOccupiedActions && can(BED_DASHBOARD_ACTIONS.PRINT_STICKER) && (
                  <Tooltip title="Print sticker">
                    <span>
                      <Button size="small" startIcon={<PrintIcon />}
                        onClick={() => setStickerId(patient.admissionId)}
                        sx={{ textTransform: "none", color: NURSE_COLORS.primary, fontSize: 12 }}>
                        Sticker
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {showOccupiedActions && can(BED_DASHBOARD_ACTIONS.TRANSFER_PATIENT) && (
                  <Tooltip title="Transfer patient">
                    <span>
                      <Button size="small" startIcon={<SwapHorizIcon />}
                        onClick={() => openTransfer(bed, patient)}
                        sx={{ textTransform: "none", color: NURSE_COLORS.primary, fontSize: 12 }}>
                        Transfer
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {showRetain && (
                  <Tooltip title="Retain (reserve) this bed">
                    <span>
                      <Button size="small" startIcon={<EventBusyIcon />}
                        onClick={() => setRetainTarget({ bedCode: bed.code })}
                        sx={{ textTransform: "none", color: NURSE_COLORS.warning, fontSize: 12 }}>
                        Retain
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {flaggedForCleaning && (
                  <Chip size="small" variant="outlined" icon={<CleaningServicesIcon />}
                    label="Needs cleaning"
                    sx={{ height: 22, fontSize: 11, color: NURSE_COLORS.info,
                          borderColor: NURSE_COLORS.info }} />
                )}
                {showClean && (
                  <Tooltip title="Mark for cleaning">
                    <span>
                      <Button size="small" startIcon={<CleaningServicesIcon />}
                        onClick={() => setCleaningBed({ bedCode: bed.code, admissionId: patient?.admissionId })}
                        sx={{ textTransform: "none", color: NURSE_COLORS.info, fontSize: 12 }}>
                        Clean
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            );
          }}
        />
      </Paper>
      )}

      {activeSection === "housekeeping" && (
      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <HousekeepingSection onDone={reload} />
      </Paper>
      )}

      {activeSection === "settings" && (
      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <HousekeepingSettingsAdmin />
      </Paper>
      )}

      {activeSection === "awaiting" && (
      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="subtitle1">Patients Awaiting Bed Allocation</Typography>
            <Typography variant="caption" color="text.secondary">
              {patients.length} patient{patients.length !== 1 ? "s" : ""} in Admitted state
            </Typography>
          </Box>
          {patients.length > 0 && (
            <Chip size="small" label={`${patients.length} pending`} color="warning" />
          )}
        </Stack>

        {patientsLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        {!patientsLoading && patients.length === 0 && (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: NURSE_COLORS.success, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No patients awaiting bed allocation.</Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          {patients.map((patient) => {
            const opts       = bedOptions[patient.id] || [];
            const isLoading  = Boolean(loadingBeds[patient.id]);
            const selectedId = selectedBeds?.[patient.id] ?? "";
            return (
              <Grid item xs={12} sm={6} md={4} xl={3} key={patient.id}>
                <Paper variant="outlined" sx={{
                  p: 2, borderRadius: 2,
                  border: `2px solid ${NURSE_COLORS.border}`,
                  transition: "all .15s ease",
                  "&:hover": { borderColor: NURSE_COLORS.accent, boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
                }}>
                  {/* Patient header */}
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: NURSE_COLORS.primary, width: 38, height: 38, fontSize: 14 }}>
                      {patient.patientName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>{patient.patientName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[patient.patientId, patient.age != null ? `${patient.age}${patient.gender}` : null].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                    <Chip size="small" label={patient.status} color="default" variant="outlined" />
                  </Stack>

                  <Divider sx={{ mb: 1.5 }} />

                  <Stack spacing={0.3} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Admitted: {patient.admissionDate}</Typography>
                    <Typography variant="caption" color="text.secondary">Location: {patient.location || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">Bed: {patient.bedCode}</Typography>
                  </Stack>

                  {/* Bed selector */}
                  <FormControl fullWidth size="small" sx={{ mb: 1,
                    "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                  }}>
                    <Select value={selectedId} displayEmpty
                      onOpen={() => fetchBedsForPatient(patient)}
                      onChange={(e) => setSelectedBeds((p) => ({ ...p, [patient.id]: e.target.value }))}
                      disabled={allocating === patient.id}
                      renderValue={(v) => {
                        if (!v) return <Typography variant="body2" color="text.secondary">{isLoading ? "Loading beds…" : "Select available bed"}</Typography>;
                        return opts.find((b) => b.bedId === v)?.label || v;
                      }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 240, borderRadius: 1.5 } } }}>
                      {!selectedId && <MenuItem value="" disabled>{isLoading ? "Loading…" : "Select available bed"}</MenuItem>}
                      {isLoading && opts.length <= 1 && (
                        <MenuItem disabled value="__loading__">
                          <CircularProgress size={12} sx={{ mr: 1 }} /> Loading…
                        </MenuItem>
                      )}
                      {opts.map((b) => (
                        <MenuItem key={b.bedId} value={b.bedId}
                          sx={{ fontWeight: b.isCurrent ? 600 : 400, color: b.isCurrent ? NURSE_COLORS.success : "inherit" }}>
                          {b.label}
                        </MenuItem>
                      ))}
                      {!isLoading && opts.length === 0 && <MenuItem disabled value="__empty__">No available beds</MenuItem>}
                    </Select>
                  </FormControl>

                  {/* Actions */}
                  <Button fullWidth variant="contained" disabled={allocating === patient.id}
                    onClick={() => handleAllocate(patient)}
                    sx={{ mb: 0.8, bgcolor: NURSE_COLORS.primary, "&:hover": { bgcolor: NURSE_COLORS.primaryDark }, borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}>
                    {allocating === patient.id ? "Saving…" : "Allocate Bed"}
                  </Button>

                  <Stack direction="row" spacing={1}>
                    {can(BED_DASHBOARD_ACTIONS.PRINT_STICKER) && (
                      <Button fullWidth size="small" variant="outlined" startIcon={<PrintIcon />}
                        onClick={() => setStickerId(patient.admissionId)}
                        sx={{ borderRadius: 1.5, textTransform: "none", color: NURSE_COLORS.primary, borderColor: NURSE_COLORS.border }}>
                        Sticker
                      </Button>
                    )}
                    {can(BED_DASHBOARD_ACTIONS.CANCEL_ADMISSION) && (
                      <Button fullWidth size="small" variant="outlined" color="error" startIcon={<CancelIcon />}
                        disabled={allocating === patient.id}
                        onClick={() => setCancelTarget(patient)}
                        sx={{ borderRadius: 1.5, textTransform: "none" }}>
                        Cancel
                      </Button>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      <BedActionDialog
        open={Boolean(activeBed)} bed={activeBed}
        patient={activeBed ? admissionByBed[activeBed.code] : null}
        onClose={() => setActiveBed(null)}
        onPrint={(id) => setStickerId(id)}
        onClean={(code, admId) => setCleaningBed({ bedCode: code, admissionId: admId })}
        onCancel={(adm) => setCancelTarget(adm)}
        onTransfer={(bed, patient) => openTransfer(bed, patient)}
        onRetain={(code) => setRetainTarget({ bedCode: code })}
        can={can}
      />
      <CancelModal
        open={Boolean(cancelTarget)} admission={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={() => { setCancelTarget(null); reloadPatients(); reload(); }}
      />
      <AllocationSuccessModal
        open={Boolean(successResult)} result={successResult}
        onPrintSticker={() => setStickerId(successResult?.admission_id)}
        onMarkCleaning={() => setCleaningBed({ bedCode: successResult?.from_bed_code, admissionId: successResult?.admission_id })}
        onRetainBed={() => setRetainTarget({ bedCode: successResult?.from_bed_code, admissionId: successResult?.admission_id })}
        onDone={() => { setSuccessResult(null); reloadPatients(); reload(); }}
      />
      <StickerPrintModal open={Boolean(stickerId)} admissionId={stickerId} onClose={() => setStickerId(null)} />
      <HousekeepingPrompt
        open={Boolean(cleaningBed)} bedCode={cleaningBed?.bedCode} admissionId={cleaningBed?.admissionId}
        onClose={() => setCleaningBed(null)}
        onDone={() => { setCleaningBed(null); reload(); }}
      />
      <RetainBedModal
        open={Boolean(retainTarget)}
        bedCode={retainTarget?.bedCode}
        admissionId={retainTarget?.admissionId}
        onClose={() => setRetainTarget(null)}
        onRetained={() => { setRetainTarget(null); setSuccessResult(null); reload(); }}
      />
      <RaiseTransferRequestModal
        open={Boolean(xfer)}
        onClose={() => setXfer(null)}
        admissionId={xfer?.admissionId}
        fromBedCode={xfer?.fromBedCode}
        fromBed={xfer?.fromBed}
        onRaised={handleRaised}
      />
      <Dialog open={Boolean(approvalNeeded)} onClose={() => setApprovalNeeded(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Approval Required</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px" }}>
            Bed <strong>{approvalNeeded?.bedCode}</strong> requires supervisor
            approval before it can be allocated. Raise a request now — a
            supervisor can approve it from the Approvals Center.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalNeeded(null)}>Not Now</Button>
          <Button variant="contained" onClick={handleRaiseApproval}>
            Raise Approval Request
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)} variant="filled">{snack.msg}</Alert> : undefined}
      </Snackbar>
      </Box>
    </Box>
    </ThemeProvider>
  );
}
